import { useRef, useState } from 'react';
import { Icon } from './Icon';
import { Avatar, StatusChip } from './bits';
import { useClients } from '../data/store';

interface Props {
  onOpen: (id: string) => void;
}

export function GlobalSearch({ onOpen }: Props) {
  const clients = useClients();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const needle = q.trim().toLowerCase();
  const results = needle
    ? clients
        .filter((c) =>
          [c.name, c.category, c.locality, c.domain, c.owner.name].some((f) => f?.toLowerCase().includes(needle)),
        )
        .slice(0, 6)
    : [];

  const open = active && !!needle;

  const pick = (id: string) => {
    onOpen(id);
    setQ('');
    setActive(false);
    setActiveIndex(0);
  };

  return (
    <div className="rs-search-wrap">
      <label className="rs-search">
        <Icon name="search" size={16} />
        <input
          placeholder="Cerca cliente…"
          aria-label="Cerca cliente"
          role="combobox"
          aria-expanded={open}
          aria-controls="rs-search-list"
          aria-activedescendant={open && results[activeIndex] ? `rs-opt-${results[activeIndex].id}` : undefined}
          aria-autocomplete="list"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActiveIndex(0);
          }}
          onFocus={() => setActive(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setActive(false), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              if (results[activeIndex]) pick(results[activeIndex].id);
            } else if (e.key === 'Escape') {
              setActive(false);
            }
          }}
        />
      </label>
      {open && (
        <div className="rs-search-menu" id="rs-search-list" role="listbox" onMouseDown={(e) => e.preventDefault()}>
          {results.length ? (
            results.map((c, i) => (
              <button
                key={c.id}
                type="button"
                id={`rs-opt-${c.id}`}
                role="option"
                aria-selected={i === activeIndex}
                className={'rs-search-opt' + (i === activeIndex ? ' is-active' : '')}
                onMouseMove={() => setActiveIndex(i)}
                onClick={() => pick(c.id)}
              >
                <Avatar client={c} size={28} />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <span className="rs-search-name">{c.name}</span>
                  <span className="rs-search-meta">
                    {c.category} · {c.locality}
                  </span>
                </span>
                <StatusChip status={c.status} />
              </button>
            ))
          ) : (
            <div className="rs-search-empty">Nessun cliente trovato</div>
          )}
        </div>
      )}
    </div>
  );
}
